import type {
	AdminEvalResult,
	EvalResultComparison,
} from "@repo/marketplace-admin"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import {
	ArrowLeft,
	Boxes,
	CircleAlert,
	Clock,
	ExternalLink,
	FlaskConical,
	Images,
	MessageSquare,
	TriangleAlert,
} from "lucide-react"
import { toast } from "sonner"
import {
	DetailPanel,
	DetailPanelSection,
	KeyValueItem,
	KeyValueList,
	PageHeader,
	Stat,
	StatStrip,
} from "@/components/layout"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { StatusDot, StatusTag } from "@/components/ui/status"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import {
	FLUSH_INNER_PANEL_CLASS,
	FLUSH_TABLE_CLASS,
	RawReportPanel,
	SampleGrid,
	SCANNER_DETAIL_PAGE_CLASS,
	ScannerDetailSplit,
	WarningsPanel,
} from "@/features/card-scanner/detail-blocks"
import { DeltaTable, VerdictBadge } from "@/features/card-scanner/eval-shared"
import { formatMetric } from "@/features/card-scanner/metrics"
import { ReasonCode, ReasonText } from "@/features/card-scanner/reason-text"
import {
	formatDuration,
	Instant,
	MODEL_TYPE_LABELS,
	type ModelType,
	QueryError,
	roleLabel,
	StatusBadge,
	TERMINAL_JOB_STATUSES,
	useScannerGate,
} from "@/features/card-scanner/shared"
import { useMarketplaceOrpc } from "@/integrations/orpc/marketplace-react"
import { cn } from "@/utils/styles.helpers"
import { describeTargets } from "./eval-jobs"

export const Route = createFileRoute("/card-scanner/eval-jobs_/$jobId")({
	component: EvalJobDetailPage,
})

function EvalJobDetailPage() {
	const allowed = useScannerGate()
	const { jobId } = Route.useParams()
	const { orpc, isAuthReady } = useMarketplaceOrpc()

	const { data, isLoading, isError, error } = useQuery({
		...orpc.cardScanner.getEvalJob.queryOptions({ input: { jobId } }),
		enabled: allowed && isAuthReady,
		refetchInterval: (query) =>
			query.state.data &&
			!TERMINAL_JOB_STATUSES.includes(query.state.data.job.status)
				? 10_000
				: false,
	})

	if (!allowed) return null

	if (isLoading) {
		return (
			<div className={SCANNER_DETAIL_PAGE_CLASS}>
				<Skeleton className="h-10 w-96" />
				<Skeleton className="h-64 w-full" />
			</div>
		)
	}
	if (isError || !data) {
		return (
			<div className={SCANNER_DETAIL_PAGE_CLASS}>
				<BackLink />
				<QueryError
					what="the eval job"
					error={error ?? new Error("Job not found")}
				/>
			</div>
		)
	}

	const { job, suite, results, comparisons } = data
	const comparisonsByResult = new Map<string, EvalResultComparison[]>()
	for (const comparison of comparisons) {
		const list = comparisonsByResult.get(comparison.resultId) ?? []
		list.push(comparison)
		comparisonsByResult.set(comparison.resultId, list)
	}
	const reportWarnings: unknown[] = Array.isArray(data.report?.warnings)
		? data.report.warnings
		: []

	return (
		<div className={SCANNER_DETAIL_PAGE_CLASS}>
			<div className="flex flex-col gap-3">
				<BackLink />
				<PageHeader
					title={
						<span className="inline-flex items-center gap-3">
							Evaluation · {describeTargets(job)}
							<StatusBadge status={job.status} />
						</span>
					}
					description={
						<span className="font-mono text-xs break-all">{job.id}</span>
					}
					actions={
						data.logUrl && (
							<Button variant="outline" asChild>
								<a href={data.logUrl} target="_blank" rel="noreferrer">
									Job log <ExternalLink className="h-3.5 w-3.5" />
								</a>
							</Button>
						)
					}
				/>
			</div>

			<ScannerDetailSplit
				aside={
					<DetailPanel title="Job" icon={Clock}>
						<DetailPanelSection>
							<KeyValueList>
								<KeyValueItem label="Suite">
									{suite ? (
										<Link
											to="/card-scanner/eval-suites/$suiteId"
											params={{ suiteId: suite.id }}
											className="hover:text-primary-text"
										>
											v{suite.version}
											{suite.current ? " (current)" : ""}
										</Link>
									) : (
										"—"
									)}
								</KeyValueItem>
								<KeyValueItem label="Trigger">
									{job.trigger.replaceAll("_", " ")}
								</KeyValueItem>
								<KeyValueItem label="Created">
									<Instant iso={job.createdAt} />
								</KeyValueItem>
								{job.startedAt && (
									<KeyValueItem label="Duration">
										{formatDuration(job.startedAt, job.finishedAt)}
									</KeyValueItem>
								)}
								{job.ec2InstanceId && (
									<KeyValueItem label="Instance" className="font-mono text-xs">
										{job.ec2InstanceId}
									</KeyValueItem>
								)}
								{job.attempt > 1 && (
									<KeyValueItem label="Attempt">#{job.attempt}</KeyValueItem>
								)}
								<KeyValueItem label="Triggered by">
									{job.triggeredBy}
								</KeyValueItem>
							</KeyValueList>
						</DetailPanelSection>
					</DetailPanel>
				}
			>
				{job.status === "failed" && job.failureReason && (
					<DetailPanel title="Failed" icon={CircleAlert}>
						<DetailPanelSection className="bg-bad-soft text-bad">
							<ReasonText reason={job.failureReason} />
						</DetailPanelSection>
					</DetailPanel>
				)}

				{job.comment && (
					<DetailPanel title="Job comments" icon={MessageSquare}>
						<DetailPanelSection>
							<pre className="whitespace-pre-wrap break-words font-mono text-muted-foreground text-xs">
								{job.comment}
							</pre>
						</DetailPanelSection>
					</DetailPanel>
				)}

				<DetailPanel
					title="Model set"
					icon={Boxes}
					meta="Targets are measured, the rest supports them"
				>
					<Table className={FLUSH_TABLE_CLASS}>
						<TableHeader>
							<TableRow>
								<TableHead>Role</TableHead>
								<TableHead>Model</TableHead>
								<TableHead />
								<TableHead>Checksum</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{Object.entries(job.modelSet).map(([role, ref]) => (
								<TableRow key={role}>
									<TableCell>{roleLabel(role)}</TableCell>
									<TableCell>
										<Link
											to="/card-scanner/models/$modelId"
											params={{ modelId: ref.modelId }}
											className="hover:text-primary-text"
										>
											{MODEL_TYPE_LABELS[ref.type as ModelType] ?? ref.type} v
											{ref.version}
										</Link>
									</TableCell>
									<TableCell>
										<span className="inline-flex items-center gap-3">
											{job.targetRoles.includes(role) && (
												<Badge variant="info">target</Badge>
											)}
											{ref.live && <StatusDot tone="ok">live</StatusDot>}
										</span>
									</TableCell>
									<TableCell>
										<CopyChip label="checksum" value={ref.checksum} />
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</DetailPanel>

				{reportWarnings.length > 0 && (
					<WarningsPanel
						noun="eval"
						warnings={reportWarnings}
						icon={TriangleAlert}
					/>
				)}

				{results.length === 0 ? (
					<DetailPanel title="Results" icon={FlaskConical}>
						<DetailPanelSection>
							<p className="text-muted-foreground">
								{TERMINAL_JOB_STATUSES.includes(job.status)
									? "No results were stored for this job."
									: "Results appear once the container uploads its report."}
							</p>
						</DetailPanelSection>
					</DetailPanel>
				) : (
					results.map((result) => (
						<ResultPanel
							key={result.id}
							result={result}
							comparisons={comparisonsByResult.get(result.id) ?? []}
						/>
					))
				)}

				{data.sampleUrls.length > 0 && (
					<DetailPanel title="Review samples" icon={Images}>
						<DetailPanelSection>
							<SampleGrid
								samples={data.sampleUrls}
								className="md:grid-cols-6"
							/>
						</DetailPanelSection>
					</DetailPanel>
				)}

				{data.report && (
					<RawReportPanel title="Raw eval report" data={data.report} />
				)}
			</ScannerDetailSplit>
		</div>
	)
}

/**
 * One result of the job as a detail panel: the set name with its counts and
 * verdicts in the header bar, then the exclusions, the overall metrics, each
 * comparison against its baseline and the per-class metrics.
 */
function ResultPanel({
	result,
	comparisons,
}: {
	result: AdminEvalResult
	comparisons: EvalResultComparison[]
}) {
	const excluded = Object.entries(result.excludedReasons ?? {})
	const notInIndex = Object.entries(result.notInIndex ?? {})
	const counts = [
		`${result.n} items`,
		`${result.resolved ?? result.n} resolved`,
		result.scored !== null && result.scored !== result.resolved
			? `${result.scored} scored`
			: null,
		`${result.errors ?? 0} errors`,
	]
		.filter(Boolean)
		.join(" · ")
	const hasChips =
		excluded.length > 0 ||
		notInIndex.length > 0 ||
		(result.unmeasuredGames && result.unmeasuredGames.length > 0)
	const failedReason = result.status === "failed" ? result.failedReason : null

	return (
		<DetailPanel
			title={<span className="font-mono">{result.setName}</span>}
			icon={FlaskConical}
			badges={
				result.status === "failed" ? (
					<StatusTag tone="bad">failed</StatusTag>
				) : null
			}
			meta={counts}
			actions={
				comparisons.length > 0 ? (
					<span className="inline-flex flex-wrap items-center justify-end gap-3">
						{comparisons.map((comparison) => (
							<span
								key={`${comparison.resultId}-${comparison.role}`}
								className="inline-flex items-center gap-1.5 text-xs"
							>
								{comparisons.length > 1 && (
									<span className="text-meta">
										{roleLabel(comparison.role)}
									</span>
								)}
								<VerdictBadge verdict={comparison.verdict} />
							</span>
						))}
					</span>
				) : undefined
			}
		>
			{(failedReason || hasChips) && (
				<DetailPanelSection>
					{failedReason && (
						<ReasonText reason={failedReason} className="text-bad" />
					)}

					{hasChips && (
						<div className="flex flex-wrap gap-1 text-xs">
							{excluded.map(([reason, count]) => (
								<ReasonCode key={reason} code={reason} suffix={` × ${count}`} />
							))}
							{notInIndex.map(([game, count]) => (
								<Badge
									key={game}
									variant="warn"
									className="font-mono font-normal"
								>
									not in index · {game} × {count}
								</Badge>
							))}
							{result.unmeasuredGames?.map((game) => (
								<Badge
									key={game}
									variant="warn"
									className="font-mono font-normal"
								>
									unmeasured · {game}
								</Badge>
							))}
						</div>
					)}
				</DetailPanelSection>
			)}

			<StatStrip className={FLUSH_INNER_PANEL_CLASS}>
				{Object.entries(result.overall).map(([key, value]) => (
					<Stat key={key} label={key} value={formatMetric(key, value)} />
				))}
			</StatStrip>

			{comparisons.map((comparison) => (
				<DetailPanelSection
					key={`${comparison.resultId}-${comparison.role}-detail`}
					label={roleLabel(comparison.role)}
					className="gap-2"
				>
					<h3 className="font-medium">
						{comparison.baseline ? (
							<>
								vs v{comparison.baseline.modelVersion} scored{" "}
								<Link
									to="/card-scanner/eval-jobs/$jobId"
									params={{ jobId: comparison.baseline.evalJobId }}
									className="hover:text-primary-text hover:underline underline-offset-2"
								>
									{new Date(comparison.baseline.createdAt).toLocaleDateString()}
								</Link>
							</>
						) : (
							<span className="font-normal text-muted-foreground">
								not comparable: {comparison.reason}
							</span>
						)}
					</h3>
					{comparison.breaches.map((breach) => (
						<p key={breach} className="font-mono text-bad text-xs">
							{breach}
						</p>
					))}
					{comparison.environmentMismatch && (
						<p className="text-warn text-xs">
							Measured on a different GPU, driver or library version than the
							baseline; platform effects may be mixed in.
						</p>
					)}
					{comparison.approximate && (
						<p className="text-warn text-xs">
							Approximate: this built set was cut after the baseline model was
							trained, so a few scored cards may have trained it.
						</p>
					)}
					{comparison.comparison && (
						<DeltaTable comparison={comparison.comparison} />
					)}
				</DetailPanelSection>
			))}

			{result.perClass && Object.keys(result.perClass).length > 0 && (
				<DetailPanelSection className="gap-0">
					<details>
						<summary className="cursor-pointer select-none text-muted-foreground text-xs">
							Per-class metrics ({Object.keys(result.perClass).length})
						</summary>
						<PerClassTable perClass={result.perClass} />
					</details>
				</DetailPanelSection>
			)}

			{result.confusion && Object.keys(result.confusion).length > 0 && (
				<DetailPanelSection className="gap-0">
					<details>
						<summary className="cursor-pointer select-none text-muted-foreground text-xs">
							Confusion matrix ({Object.keys(result.confusion).length} classes)
						</summary>
						<ConfusionTable confusion={result.confusion} />
					</details>
				</DetailPanelSection>
			)}
		</DetailPanel>
	)
}

/** Truth rows × predicted columns; the diagonal is the correct answers. */
function ConfusionTable({
	confusion,
}: {
	confusion: Record<string, Record<string, number>>
}) {
	const classes = [
		...new Set([
			...Object.keys(confusion),
			...Object.values(confusion).flatMap((row) => Object.keys(row)),
		]),
	].sort()

	return (
		<div className="mt-2 overflow-x-auto">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead className="text-meta text-xs">
							Actual ↓ · Predicted →
						</TableHead>
						{classes.map((predicted) => (
							<TableHead key={predicted} className="text-right font-mono">
								{predicted}
							</TableHead>
						))}
						<TableHead className="text-right">Correct</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{classes.map((truth) => {
						const row = confusion[truth] ?? {}
						const total = Object.values(row).reduce(
							(sum, count) => sum + count,
							0,
						)
						const correct = row[truth] ?? 0
						return (
							<TableRow key={truth}>
								<TableHead scope="row" className="font-mono text-xs">
									{truth}
								</TableHead>
								{classes.map((predicted) => {
									const count = row[predicted] ?? 0
									const diagonal = predicted === truth
									return (
										<TableCell
											key={predicted}
											className={cn(
												"text-right tabular-nums",
												count === 0 && "text-meta",
												diagonal && count > 0 && "font-medium",
												!diagonal && count > 0 && "bg-bad-soft text-bad",
											)}
										>
											{count > 0 ? count : "·"}
										</TableCell>
									)
								})}
								<TableCell className="text-right text-muted-foreground tabular-nums">
									{total > 0 ? formatMetric("accuracy", correct / total) : "—"}
								</TableCell>
							</TableRow>
						)
					})}
				</TableBody>
			</Table>
		</div>
	)
}

function PerClassTable({
	perClass,
}: {
	perClass: Record<string, Record<string, number>>
}) {
	const keys = [
		...new Set(Object.values(perClass).flatMap((block) => Object.keys(block))),
	].sort()
	return (
		<Table className="mt-2">
			<TableHeader>
				<TableRow>
					<TableHead>Class</TableHead>
					{keys.map((key) => (
						<TableHead key={key} className="text-right">
							{key}
						</TableHead>
					))}
				</TableRow>
			</TableHeader>
			<TableBody>
				{Object.entries(perClass).map(([className, block]) => (
					<TableRow key={className}>
						<TableCell className="font-mono text-xs">{className}</TableCell>
						{keys.map((key) => {
							const value = block[key]
							return (
								<TableCell
									key={key}
									className={
										value === undefined
											? "text-right text-meta"
											: "text-right tabular-nums"
									}
								>
									{value === undefined ? "—" : formatMetric(key, value)}
								</TableCell>
							)
						})}
					</TableRow>
				))}
			</TableBody>
		</Table>
	)
}

function BackLink() {
	return (
		<Link
			to="/card-scanner/eval-jobs"
			className="inline-flex w-fit items-center gap-1 text-meta text-xs hover:text-foreground"
		>
			<ArrowLeft className="h-3.5 w-3.5" /> Eval jobs
		</Link>
	)
}

function CopyChip({ label, value }: { label: string; value: string }) {
	return (
		<button
			type="button"
			className="max-w-56 truncate font-mono text-muted-foreground text-xs hover:text-primary-text"
			title={`${value} (click to copy)`}
			onClick={() => {
				navigator.clipboard
					.writeText(value)
					.then(() => toast.success(`${label} copied`))
					.catch(() => toast.error(`Could not copy ${label}`))
			}}
		>
			{value}
		</button>
	)
}
